// Product analytics (PostHog).
//
// Goal: understand what visitors do on the site — especially which steps of the
// public demo they reach (landing → start lesson → start exercises → finish →
// click "create account") so we can read funnels against the Meta/Reddit ad spend.
//
// Privacy posture (chosen deliberately): COOKIELESS. We use localStorage for the
// anonymous distinct_id instead of cookies, honour Do-Not-Track, and mask all
// input/text in any future session recording. This keeps us out of cookie-banner
// territory for a small app while still giving usable funnels. Recording is off
// by default; flip `disable_session_recording` below if you want it later.
//
// Config comes from <meta> tags rendered by the Rails layout (see
// app/views/layouts/application.html.erb), so the key/host are env-driven and the
// integration stays dormant in dev/test where the meta tags are absent.

import posthog from 'posthog-js'

let enabled = false

function metaContent(name) {
  const el = document.querySelector(`meta[name="${name}"]`)
  return el ? el.getAttribute('content') : null
}

// Call once on boot. No-ops (and `track` becomes a no-op) unless a project key
// is present, so nothing is sent in development/test or before PostHog is set up.
export function initAnalytics() {
  if (enabled) return
  const key = metaContent('posthog-key')
  if (!key) return

  const host = metaContent('posthog-host') || 'https://us.i.posthog.com'

  posthog.init(key, {
    api_host: host,
    persistence: 'localStorage',      // cookieless: no cookies, id lives in localStorage
    respect_dnt: true,                // skip users who set Do-Not-Track
    autocapture: true,                // automatic clicks/pageviews; explicit events below add funnel clarity
    capture_pageview: true,
    disable_session_recording: true,  // off by default for privacy; safe to enable later
    session_recording: { maskAllInputs: true, maskTextSelector: '*' },
  })

  enabled = true

  // Tie the anonymous person (who browsed the landing page / demo) to their
  // account once signed in, so the funnel connects pre- and post-signup. We
  // identify by user id only — no email/PII, in keeping with the cookieless posture.
  const userId = metaContent('analytics-user-id')
  if (userId) posthog.identify(userId)

  // Account dialect as a super property: every event carries it, so funnels
  // and retention can segment the Hanoi beta without per-event plumbing.
  const dialect = metaContent('analytics-dialect')
  if (dialect) posthog.register({ dialect })

  // The server sets this one-shot flag on the first page render after a brand-new
  // account is created (email/password or Google), so the funnel closes on a real
  // signup, not just the button click.
  if (metaContent('analytics-signup-completed') === 'true') {
    track('signup_completed', { method: metaContent('analytics-signup-method') || 'unknown' })
  }
}

// Fire a named event. Safe to call anywhere — it's a no-op until initAnalytics()
// has run with a real key, so demo components don't need to know about config.
export function track(event, properties = {}) {
  if (!enabled) return
  try {
    posthog.capture(event, properties)
  } catch (_e) {
    // Never let analytics break the app.
  }
}
